import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isMacPlatform } from './platform';

type CloseOptions = {
  // `false` on a close that unmounts the element which had focus — a navigation
  // started from the palette. Restoring then either targets a detached node or
  // steals focus from the page that just arrived.
  readonly restoreFocus?: boolean;
};

type CommandPaletteContextValue = {
  readonly open: boolean;
  readonly query: string;
  readonly restoreFocusOnClose: boolean;
  readonly setQuery: (query: string) => void;
  readonly openPalette: () => void;
  readonly close: (options?: CloseOptions) => void;
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * Owns the palette's open state for the whole authenticated shell, so the topbar
 * entry, the keyboard shortcut and route changes all drive the same palette.
 * Mounted inside the router — it reads the location to close on navigation.
 */
export function CommandPaletteProvider({ children }: { readonly children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [restoreFocusOnClose, setRestoreFocusOnClose] = useState(true);
  const location = useLocation();
  const openRef = useRef(open);

  openRef.current = open;

  const openPalette = useCallback(() => {
    setRestoreFocusOnClose(true);
    setQuery('');
    setOpen(true);
  }, []);

  const close = useCallback((options?: CloseOptions) => {
    setRestoreFocusOnClose(options?.restoreFocus ?? true);
    setOpen(false);
  }, []);

  // One listener for the whole shell. It deliberately does NOT bow out while a
  // text input has focus (parent DA 6): ⌘K is not a text-editing binding, so
  // there is nothing to protect, and an agent mid-way through a filter field is
  // exactly who wants to jump somewhere else.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Auto-repeat while the chord is held would toggle the palette dozens of
      // times a second; only the first keydown counts.
      if (event.repeat) return;

      if (event.key !== 'k' && event.key !== 'K') return;

      const modifier = isMacPlatform() ? event.metaKey : event.ctrlKey;

      // `Shift` is excluded rather than ignored: ⌘/Ctrl+Shift+K is a different
      // binding (the browser's own console, among others), and claiming it
      // would take a chord this feature was never given.
      if (!modifier || event.altKey || event.shiftKey) return;

      // Always claim the chord, so the browser's own binding never also fires.
      event.preventDefault();

      if (openRef.current) {
        close();

        return;
      }

      openPalette();
    };

    // Capture phase (BL-023): the shortcut must work from anywhere, including
    // inside a dialog or menu whose own handlers stop propagation before the
    // event ever reaches `document` on the way back up.
    document.addEventListener('keydown', onKeyDown, true);

    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [close, openPalette]);

  // Every row in the palette navigates, so closing on the route change makes it
  // impossible for a future row type to forget. Focus is not restored: the
  // element that had it is unmounting with the page being left.
  useEffect(() => {
    if (!openRef.current) return;

    close({ restoreFocus: false });
  }, [location.pathname, location.search, close]);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, query, restoreFocusOnClose, setQuery, openPalette, close }),
    [open, query, restoreFocusOnClose, openPalette, close],
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}

export function useCommandPalette(): CommandPaletteContextValue {
  const value = useContext(CommandPaletteContext);

  if (value === null) throw new Error('useCommandPalette must be used inside a CommandPaletteProvider');

  return value;
}
